import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

// Icons
import { IoArrowBack } from "react-icons/io5";

// Types
import type { IInvestorProps } from "./types";

// Models
import type { IMBatche } from "~/models/Batche";
import type { IMRealStateProduct } from "~/models/RealEstateProduct";
import type { IMRealEstateProductsHouse } from "~/models/RealEstateProductHouse";


// Services
import api from "~/services/api";

// Partials
import { Partials } from "./Partials";

export function InvestorPlus(props: IInvestorProps) {
    const [batchAmount, setBatchAmount] = useState<number>(0) // B4
    const [brokerageAmount, setBrokerageAmount] = useState<number>(0) // A32
    const [feeAmount, setFeeAmount] = useState<number>(0) // A26
    const [idealAreaBuild, setIdealAreaBuild] = useState<number>(0) // B12
    const [houseAmount, setHouseAmount] = useState<number>(0) // B21
    const [houseAmountCache, setHouseAmountCache] = useState<number>(0) // B21
    const [timeToBuildSell, setTimeToBuildSell] = useState<number>(0) // B37
    const [condominiumAmount, setCondominiumAmount] = useState<number>(0) // B41
    const [extraAmount, setExtraAmount] = useState<number>(0) // B49
    const [dispatcher] = useState<number>(2000.0) // B48

    // AUX Variables
    const REP_PROJECT: IMRealStateProduct | null = useMemo(() => JSON.parse(sessionStorage.getItem('REP@PROJECT') || '{}') || null, [])
    const REP_BUILD_DATA: {
        houseAmount: number
        idealArea: number
        idealAreaBuild: number,
        AMOUNT_BY_METERS: string,
        VGV: {
            [key: number]: {
                vgv: number;
                house_area: number;
                terrain_area: number;
            }[];
        }
    } | null = useMemo(() => JSON.parse(sessionStorage.getItem('REP@BUILD_DATA') || '{}') || null, [])
    const REP_HOUSES: IMRealEstateProductsHouse[] | null = useMemo(() => JSON.parse(sessionStorage.getItem('REP@HOUSES') || '{}') || null, [])

    const BUILD_AMOUNT = 40000.00; // B13 - INTEGRATION WITH 'MONTE SUA CASA'

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    const ADM_FEE = useMemo(() => (BUILD_AMOUNT * idealAreaBuild) * 0.1, [BUILD_AMOUNT, idealAreaBuild]) // B14

    const ITBI = useMemo(() => {
        return batchAmount * 0.02
    }, [batchAmount]) //B5

    const CERTIFICATIONS = useMemo(() => {
        return batchAmount * 0.013
    }, [batchAmount])  // B7

    const TOTAL_TERRAIN = useMemo(() => {
        return batchAmount + CERTIFICATIONS
    }, [batchAmount, CERTIFICATIONS]) //B9

    // biome-ignore lint/correctness/useExhaustiveDependencies:
    const TOTAL_BUILD = useMemo(() => {
        return BUILD_AMOUNT * idealAreaBuild
    }, [BUILD_AMOUNT, idealAreaBuild]) // B16

    const REGISTER = useMemo(() => {
        return TOTAL_BUILD * 0.01
    }, [TOTAL_BUILD]) // B6

    async function handlerGetBatchAmount() {
        try {
            const response = await api.get<IMBatche[]>(`builders/real-estate-products/${REP_PROJECT?.id || -1}/terrains`);

            if (response.status !== 200) throw new Error('');;

            const VGV_BATCHES_AMOUNT = response.data.reduce((d, b) => d + b.amount, 0)

            setBatchAmount(VGV_BATCHES_AMOUNT)
        } catch (error) {
            // do anything
        }
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: Its not necessary to add the dependencies here
    useEffect(() => {
        handlerGetBatchAmount()

        return () => {
            setBatchAmount(0)
        }
    }, [])

    useEffect(() => {
        if (!REP_BUILD_DATA) return;

        setIdealAreaBuild(REP_BUILD_DATA?.idealAreaBuild || 0)
        setHouseAmount(REP_BUILD_DATA?.houseAmount || 0)
        setHouseAmountCache(REP_BUILD_DATA?.houseAmount || 0)

        return () => {
            setIdealAreaBuild(0)
            setHouseAmount(0)
            setHouseAmountCache(0)
        }
    }, [REP_BUILD_DATA])

    return (
        <div id="investor-plus-container" className="max-sm:!w-full h-full md:h-screen flex flex-col max-sm:items-center gap-2 md:ml-[106px] p-4"
            style={{
                width: 'calc(100vw - 106px)',
            }}
        >

            <div className="w-full h-auto flex flex-col max-sm:pt-16 max-sm:items-center max-sm:justify-center gap-4">

                <span id="investor-plus-header" className="font-bold text-xl flex flex-row items-center gap-4">
                    <Link to="../">
                        <IoArrowBack />
                    </Link>
                    {REP_PROJECT?.name || 'N/A'}
                </span>

                <div id="investor-plus-content" className="w-full h-auto flex flex-row flex-wrap gap-2 gap-x-4 justify-between">

                    <Partials.Terrain
                        CERTIFICATIONS={CERTIFICATIONS}
                        TOTAL_TERRAIN={TOTAL_TERRAIN}
                        ITBI={ITBI}
                        REGISTER={REGISTER}
                        batchAmount={batchAmount}
                        setBatchAmount={setBatchAmount}
                    />

                    <Partials.Build
                        ADM_FEE={ADM_FEE}
                        BUILD_AMOUNT={BUILD_AMOUNT}
                        idealAreaBuild={idealAreaBuild}
                        setIdealAreaBuild={setIdealAreaBuild}
                        TOTAL_BUILD={TOTAL_BUILD}
                    />

                    <Partials.VGV
                        houseAmount={houseAmount}
                        houseAmountCache={houseAmountCache}
                        setHouseAmount={setHouseAmount}
                        TOTAL_BUILD={TOTAL_BUILD}
                    />

                    <Partials.Brokerage
                        houseAmount={houseAmount}
                        setHouseAmount={setHouseAmount}
                        TOTAL_BUILD={TOTAL_BUILD}
                        setBrokerageAmount={setBrokerageAmount}
                    />

                    <Partials.Personal
                        setTimeToBuildSell={setTimeToBuildSell}
                        timeToBuildSell={timeToBuildSell}
                        personaF={(TOTAL_BUILD - houseAmount) * 0.15}
                        personaJP={houseAmount * 0.6}
                        personaJR={houseAmount * 0.4}
                        setFeeAmount={setFeeAmount}
                    />

                    <Partials.CST
                        setTimeToBuildSell={setTimeToBuildSell}
                        timeToBuildSell={timeToBuildSell}
                    />


                    <Partials.Extra
                        REP_PROJECT={REP_PROJECT}
                        REP_BUILD_DATA={REP_BUILD_DATA}
                        REP_HOUSES={REP_HOUSES || []}
                        condominiumAmount={condominiumAmount}
                        dispatcher={dispatcher}
                        extraAmount={extraAmount}
                        setCondominiumAmount={setCondominiumAmount}
                        setExtraAmount={setExtraAmount}
                        timeToBuildSell={timeToBuildSell}
                        houseAmount={houseAmount}
                        TOTAL_BUILD={TOTAL_BUILD}
                        ADM_FEE={ADM_FEE}
                        CERTIFICATIONS={CERTIFICATIONS}
                        TOTAL_TERRAIN={TOTAL_TERRAIN}
                        ITBI={ITBI}
                        REGISTER={REGISTER}
                    />

                    <Partials.Result
                        houseAmount={houseAmount}
                        extraAmount={extraAmount}
                        brokerageAmount={brokerageAmount}
                        feeAmount={feeAmount}
                        TOTAL_BUILD={TOTAL_BUILD}
                        TOTAL_TERRAIN={TOTAL_TERRAIN}
                    />
                </div>
            </div>
        </div>
    )
}
